// Mojo Project Pool picker — read-only File Vault view scoped to
// ``admin/marketing-templates/project-thumbnails/``. Opens from the
// Marketing+ section image slot. On "Use image" it asks the backend
// to mint a permanent share-link and returns it to the caller which
// drops the URL onto the current section (bypassing the cropper).
//
// Deliberately isolated from the main File Vault page so the picker
// can't accidentally expose management controls (upload, rename,
// delete, move, new folder) or navigate above the pool root — the
// backend enforces the same guardrails on every request.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { X, Loader2, Folder, ChevronRight, Search, LayoutGrid, List, Image as ImageIcon, CheckCircle2 } from "lucide-react";

const POOL_ROOT = "admin/marketing-templates/project-thumbnails/";
const ROOT_LABEL = "Project Thumbnails";

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// Turn a full pool prefix into breadcrumb crumbs like
// [{label:"Project Thumbnails",prefix:POOL_ROOT},{label:"Autumn",prefix:...}]
function crumbsFor(prefix) {
  const trimmed = prefix.replace(POOL_ROOT, "").replace(/\/$/, "");
  const parts = trimmed ? trimmed.split("/") : [];
  const crumbs = [{ label: ROOT_LABEL, prefix: POOL_ROOT }];
  let acc = POOL_ROOT;
  for (const part of parts) {
    acc += `${part}/`;
    crumbs.push({ label: part, prefix: acc });
  }
  return crumbs;
}

export default function ProjectPoolPickerModal({ open, onClose, onSelect }) {
  const [prefix, setPrefix] = useState(POOL_ROOT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectingKey, setSelectingKey] = useState("");
  const searchTimer = useRef(null);

  const crumbs = useMemo(() => crumbsFor(prefix), [prefix]);

  const loadFolder = useCallback(async (nextPrefix) => {
    setLoading(true); setError(""); setSearchResults(null); setSearch("");
    try {
      const { data } = await api.get("/portal/marketing/project-pool/browse", {
        params: { prefix: nextPrefix },
      });
      setFolders(data.folders || []);
      setFiles(data.files || []);
      setPrefix(data.prefix || nextPrefix);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load Project Pool");
      setFolders([]); setFiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load once when the modal opens; reset back to the root each
  // fresh open so a stale deep-folder state can't confuse the user.
  useEffect(() => {
    if (!open) return;
    loadFolder(POOL_ROOT);
  }, [open, loadFolder]);

  // Debounced folder-scoped search. Clears results when input is
  // empty so we fall back to the plain listing.
  useEffect(() => {
    if (!open) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const term = search.trim();
    if (term.length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/portal/marketing/project-pool/search", {
          params: { q: term, prefix },
        });
        setSearchResults(data.files || []);
      } catch (e) {
        setError(e?.response?.data?.detail || "Search failed");
        setSearchResults([]);
      } finally { setSearching(false); }
    }, 250);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [search, prefix, open]);

  const handleUseImage = async (file) => {
    setSelectingKey(file.key); setError("");
    try {
      const { data } = await api.post("/portal/marketing/project-pool/select", { key: file.key });
      onSelect({ image_url: data.image_url, image_key: data.key, name: data.name });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not add image");
      setSelectingKey("");
    }
  };

  if (!open) return null;

  const displayFiles = searchResults != null ? searchResults : files;
  const showingSearch = searchResults != null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-stone-900/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto"
      data-testid="project-pool-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-pool-title"
    >
      <div className="relative w-full max-w-5xl m-6 bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col max-h-[calc(100vh-3rem)]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4">
          <div>
            <h2 id="project-pool-title" className="text-lg font-bold text-stone-900 flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-stone-700" />
              Add image from Mojo Project Pool
            </h2>
            <p className="text-xs text-stone-500 mt-0.5">Read-only — curated by Head Office.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-stone-700 p-1 rounded-md"
            data-testid="project-pool-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar: breadcrumbs + search + view toggle */}
        <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-stone-100 bg-stone-50/60">
          <nav className="flex items-center flex-wrap gap-1 text-sm text-stone-600" aria-label="Breadcrumb" data-testid="project-pool-breadcrumbs">
            {crumbs.map((c, i) => (
              <span key={c.prefix} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-stone-400" />}
                <button
                  type="button"
                  onClick={() => loadFolder(c.prefix)}
                  disabled={i === crumbs.length - 1}
                  data-testid={`project-pool-crumb-${i}`}
                  className={`px-2 py-1 rounded-md text-sm font-medium ${
                    i === crumbs.length - 1
                      ? "text-stone-900 cursor-default"
                      : "text-stone-600 hover:bg-stone-200/60"
                  }`}
                >
                  {c.label}
                </button>
              </span>
            ))}
          </nav>
          <div className="flex items-center gap-2 ml-auto">
            <div className="relative">
              <Search className="w-4 h-4 text-stone-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search in current folder"
                data-testid="project-pool-search"
                className="pl-8 pr-3 py-1.5 text-sm border border-stone-300 rounded-lg w-56 focus:outline-none focus:ring-2 focus:ring-stone-400"
              />
            </div>
            <div className="inline-flex rounded-lg border border-stone-300 overflow-hidden">
              <button
                type="button"
                onClick={() => setView("grid")}
                data-testid="project-pool-view-grid"
                aria-pressed={view === "grid"}
                className={`px-2 py-1.5 text-xs ${view === "grid" ? "bg-stone-900 text-white" : "text-stone-700 hover:bg-stone-100"}`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setView("list")}
                data-testid="project-pool-view-list"
                aria-pressed={view === "list"}
                className={`px-2 py-1.5 text-xs ${view === "list" ? "bg-stone-900 text-white" : "text-stone-700 hover:bg-stone-100"}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="project-pool-body">
          {error && (
            <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="project-pool-error">
              {error}
            </div>
          )}
          {loading || searching ? (
            <div className="flex items-center justify-center py-16 text-stone-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : (
            <>
              {/* Folders row — only when not searching */}
              {!showingSearch && folders.length > 0 && (
                <div className="mb-5">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-stone-500 mb-2">Folders</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                    {folders.map((f) => (
                      <button
                        key={f.prefix}
                        type="button"
                        onClick={() => loadFolder(f.prefix)}
                        data-testid={`project-pool-folder-${f.name}`}
                        className="flex items-center gap-2 px-3 py-2 border border-stone-200 rounded-lg hover:bg-stone-50 text-left"
                      >
                        <Folder className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        <span className="text-sm text-stone-800 truncate">{f.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Files */}
              <div className="text-[11px] uppercase tracking-wider font-bold text-stone-500 mb-2">
                {showingSearch ? `Search results (${displayFiles.length})` : `Images (${displayFiles.length})`}
              </div>
              {displayFiles.length === 0 ? (
                <div className="text-sm text-stone-500 py-10 text-center border border-dashed border-stone-200 rounded-lg" data-testid="project-pool-empty">
                  {showingSearch ? "No images match that search." : "No images in this folder yet."}
                </div>
              ) : view === "grid" ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="project-pool-grid">
                  {displayFiles.map((file) => (
                    <div
                      key={file.key}
                      className="border border-stone-200 rounded-xl overflow-hidden bg-white flex flex-col"
                      data-testid={`project-pool-file-${file.name}`}
                    >
                      <div className="aspect-[4/3] bg-stone-100 overflow-hidden flex items-center justify-center">
                        <img
                          src={file.thumb_url}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(ev) => { ev.currentTarget.style.display = "none"; }}
                        />
                      </div>
                      <div className="px-2.5 py-2 flex flex-col gap-1.5">
                        <div className="text-xs text-stone-800 truncate" title={file.name}>{file.name}</div>
                        <div className="text-[10px] text-stone-500 uppercase tracking-wider">{formatSize(file.size)}</div>
                        <button
                          type="button"
                          onClick={() => handleUseImage(file)}
                          disabled={selectingKey === file.key}
                          data-testid={`project-pool-use-${file.name}`}
                          className="mt-1 inline-flex items-center justify-center gap-1 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider bg-stone-900 text-white rounded-md hover:bg-stone-800 disabled:opacity-60"
                        >
                          {selectingKey === file.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                          Use image
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="divide-y divide-stone-100 border border-stone-200 rounded-xl overflow-hidden" data-testid="project-pool-list">
                  {displayFiles.map((file) => (
                    <div
                      key={file.key}
                      className="flex items-center gap-3 px-3 py-2 bg-white"
                      data-testid={`project-pool-file-${file.name}`}
                    >
                      <div className="w-10 h-10 bg-stone-100 rounded overflow-hidden flex-shrink-0">
                        <img src={file.thumb_url} alt="" className="w-full h-full object-cover" loading="lazy"
                          onError={(ev) => { ev.currentTarget.style.display = "none"; }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-stone-800 truncate">{file.name}</div>
                        <div className="text-[10px] text-stone-500 uppercase tracking-wider">{formatSize(file.size)}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleUseImage(file)}
                        disabled={selectingKey === file.key}
                        data-testid={`project-pool-use-${file.name}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider bg-stone-900 text-white rounded-md hover:bg-stone-800 disabled:opacity-60"
                      >
                        {selectingKey === file.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Use image
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
